import { 
    getFirestore, 
    doc, 
    getDoc,
    collection,
    getDocs,
    query,
    where,
    orderBy,
    limit 
} from 'firebase/firestore';

const db = getFirestore();

/**
 * 🚀 Firestore Page Loader
 * 
 * Lädt Pages aus dem neuen Firestore Publishing System
 * Fallback zum RTDB System für Backward Compatibility
 */

/**
 * Load page data from Firestore with fallback to RTDB
 */
export const loadPageFromFirestore = async (username, pagename, rtdbFallback) => {
    console.log('🔍 loadPageFromFirestore:', { username, pagename });
    
    try {
        // 1. Get space info by username
        const spaceInfo = await getSpaceInfoByUsername(username);
        if (!spaceInfo) {
            console.log('❌ Space not found in Firestore:', username);
            return await rtdbFallback();
        }
        
        // 2. Determine page to load
        const pageKey = !pagename ? 'home' : pagename;
        
        // 3. Load from Firestore sites collection
        const siteDoc = await getDoc(doc(db, 'sites', spaceInfo.spaceId));
        
        if (!siteDoc.exists()) {
            console.log('⚠️ Site not found in Firestore, trying RTDB fallback');
            return await rtdbFallback();
        }
        
        const siteData = siteDoc.data();
        
        // 4. Check if site is published
        if (!siteData.publishedVersion || siteData.status !== 'live') {
            console.log('⚠️ Site not published in Firestore, trying RTDB fallback');
            return await rtdbFallback();
        }
        
        // 5. Load rendered content for current version
        const renderedDoc = await getDoc(
            doc(db, 'sites', spaceInfo.spaceId, 'rendered', siteData.publishedVersion)
        );
        
        if (!renderedDoc.exists()) {
            console.log('⚠️ Rendered content not found, loading from version snapshot');
            return await loadFromVersionSnapshot(spaceInfo.spaceId, siteData.publishedVersion, pageKey);
        }
        
        const renderedData = renderedDoc.data();
        const pageSlug = pageKey === 'home' ? 'index' : pageKey;
        const pageContent = renderedData.pages?.[pageSlug];
        
        if (!pageContent) {
            console.log('⚠️ Page not found in rendered content, trying version snapshot');
            return await loadFromVersionSnapshot(spaceInfo.spaceId, siteData.publishedVersion, pageKey);
        }
        
        // 6. Transform to expected format
        const transformedPageData = transformFirestorePageData(pageContent, siteData);
        
        console.log('✅ Page loaded from Firestore rendered content:', transformedPageData);
        
        const siteInfo = {
            ...siteData,
            // Include branding info for loading screen
            customLogo: siteData.customLogo,
            customLogoWidth: siteData.customLogoWidth,
            customLogoHeight: siteData.customLogoHeight,
            showCustomLogo: siteData.showCustomLogo,
            siteName: siteData.siteName || siteData.name
        };
        
        console.log('🔍 firestorePageLoader: siteInfo created:', {
            customLogo: siteInfo.customLogo ? 'present' : 'missing',
            siteName: siteInfo.siteName,
            siteInfoKeys: Object.keys(siteInfo)
        });
        
        return {
            pageData: transformedPageData,
            source: 'firestore-rendered',
            version: siteData.publishedVersion,
            siteInfo
        };
        
    } catch (error) {
        console.error('❌ Error loading from Firestore:', error);
        console.log('🔄 Falling back to RTDB...');
        return await rtdbFallback();
    }
};

/**
 * Load from version snapshot if rendered content not available
 */
const loadFromVersionSnapshot = async (siteId, versionId, pageKey) => {
    try {
        const versionDoc = await getDoc(
            doc(db, 'sites', siteId, 'versions', versionId)
        );
        
        if (!versionDoc.exists()) {
            throw new Error('Version snapshot not found');
        }
        
        const versionData = versionDoc.data();
        const snapshot = versionData.snapshot;
        
        // Check if this is the requested page
        if (snapshot?.metadata?.pageName === pageKey || 
            (pageKey === 'home' && snapshot?.metadata?.isHomePage)) {
            
            const transformedPageData = {
                ...snapshot.metadata,
                ...snapshot.content.globalSettings,
                widgets: snapshot.content.blocks || [],
                seoSettings: snapshot.seo,
                // Add version info
                publishedVersion: versionId,
                publishedAt: versionData.publishedAt?.toDate()
            };
            
            console.log('✅ Page loaded from Firestore version snapshot:', transformedPageData);
            return {
                pageData: transformedPageData,
                source: 'firestore-snapshot',
                version: versionId
            };
        }
        
        throw new Error('Page not found in version snapshot');
        
    } catch (error) {
        console.error('❌ Error loading from version snapshot:', error);
        throw error;
    }
};

/**
 * Get space info by username (spacename)
 */
const getSpaceInfoByUsername = async (username) => {
    try {
        // Query sites collection by username
        const sitesQuery = query(
            collection(db, 'sites'),
            where('username', '==', username),
            where('status', '==', 'live'),
            limit(1)
        );
        
        const sitesSnapshot = await getDocs(sitesQuery);
        
        if (sitesSnapshot.empty) {
            return null;
        }
        
        const siteDoc = sitesSnapshot.docs[0];
        return {
            spaceId: siteDoc.id,
            ...siteDoc.data()
        };
        
    } catch (error) {
        console.error('❌ Error getting space info:', error);
        return null;
    }
};

/**
 * Transform Firestore page data to expected RTDB format
 */
const transformFirestorePageData = (firestorePageData, siteData) => {
    const { metadata, content, seo } = firestorePageData;
    
    return {
        // Page metadata
        name: metadata?.name || siteData.name,
        slug: metadata?.slug || '/',
        userId: metadata?.userId || siteData.userId,
        username: metadata?.username || siteData.username,
        spaceId: metadata?.spaceId || siteData.spaceId,
        isHomePage: metadata?.isHomePage || false,
        
        // Content
        widgets: content?.blocks || [],
        
        // Global settings
        globalBigFont: content?.globalSettings?.fonts?.globalBigFont || 'Jost-Bold',
        globalSmallFont: content?.globalSettings?.fonts?.globalSmallFont || 'Jost-Regular',
        colors: content?.globalSettings?.colors || {},
        backgroundColor: content?.globalSettings?.backgroundColor || '#FFFFFF',
        
        // 🔧 CRITICAL FIX: Include custom fonts from Firestore
        customFonts: content?.globalSettings?.customFonts || [],
        userCustomFonts: content?.globalSettings?.userCustomFonts || [],
        
        // 🔥 NEW: Include navigation settings from Firestore
        navBarSettings: content?.globalSettings?.navBarSettings || null,
        navigation: content?.globalSettings?.navigation || null, // ✅ NEW 2025: Modern Navigation System
        
        // SEO
        seoSettings: seo || {},
        
        // Publishing info
        isActive: true, // Always true for published Firestore pages
        publishedAt: siteData.publishedAt,
        publishedVersion: siteData.publishedVersion,
        
        // Source tracking
        _firestoreSource: true,
        _version: siteData.publishedVersion,
        
        // Include space branding data for fallback
        space: {
            customLogo: siteData.customLogo,
            customLogoWidth: siteData.customLogoWidth,
            customLogoHeight: siteData.customLogoHeight,
            showCustomLogo: siteData.showCustomLogo,
            siteName: siteData.siteName || siteData.name,
            spaceName: siteData.name
        }
    };
};

/**
 * Load custom domain page from Firestore
 */
export const loadCustomDomainPageFromFirestore = async (domain, path) => {
    console.log('🌐 loadCustomDomainPageFromFirestore:', { domain, path });
    
    try {
        // Query sites by custom domain
        const sitesQuery = query(
            collection(db, 'sites'),
            where('customDomain', '==', domain),
            where('status', '==', 'live'),
            limit(1)
        );
        
        const sitesSnapshot = await getDocs(sitesQuery);
        
        if (sitesSnapshot.empty) {
            console.log('❌ No site found for custom domain:', domain);
            return null;
        }
        
        const siteDoc = sitesSnapshot.docs[0];
        const siteData = siteDoc.data();
        
        // Determine which page to load based on path
        let pageKey = 'home'; // Default to home page
        if (path && path !== '/' && path !== '') {
            pageKey = path.replace(/^\//, '').replace(/\/$/, '') || 'home';
        }
        
        // Load rendered content
        const renderedDoc = await getDoc(
            doc(db, 'sites', siteDoc.id, 'rendered', siteData.publishedVersion)
        );
        
        if (!renderedDoc.exists()) {
            console.log('⚠️ Rendered content not found for custom domain');
            return null;
        }
        
        const renderedData = renderedDoc.data();
        const pageSlug = pageKey === 'home' ? 'index' : pageKey;
        const pageContent = renderedData.pages?.[pageSlug];
        
        if (!pageContent) {
            console.log('❌ Page not found for path:', path);
            return null;
        }
        
        const transformedPageData = transformFirestorePageData(pageContent, siteData);
        
        console.log('✅ Custom domain page loaded from Firestore:', transformedPageData);
        return {
            pageData: transformedPageData,
            source: 'firestore-custom-domain',
            version: siteData.publishedVersion,
            siteInfo: siteData
        };
        
    } catch (error) {
        console.error('❌ Error loading custom domain page from Firestore:', error);
        return null;
    }
};

/**
 * Get site SEO data for meta rendering
 */
export const getSiteSEOData = async (siteId, versionId) => {
    try {
        const versionDoc = await getDoc(
            doc(db, 'sites', siteId, 'versions', versionId)
        );
        
        if (!versionDoc.exists()) {
            return null;
        }
        
        const versionData = versionDoc.data();
        return {
            seo: versionData.snapshot?.seo || {},
            metadata: versionData.snapshot?.metadata || {},
            space: versionData.snapshot?.space || {}
        };
        
    } catch (error) {
        console.error('❌ Error loading SEO data:', error);
        return null;
    }
};

/**
 * Check if site exists and is published in Firestore
 */
export const checkFirestoreSiteStatus = async (username) => {
    try {
        const spaceInfo = await getSpaceInfoByUsername(username);
        
        if (!spaceInfo) {
            return { exists: false, published: false };
        }
        
        return {
            exists: true,
            published: spaceInfo.status === 'live' && spaceInfo.publishedVersion,
            siteId: spaceInfo.spaceId,
            version: spaceInfo.publishedVersion
        };
        
    } catch (error) {
        console.error('❌ Error checking Firestore site status:', error);
        return { exists: false, published: false };
    }
};
